import { LinearGradient } from "expo-linear-gradient";

import { DateFormat } from "@/constants/DateFormat";
import { SMTWeekLogLayout } from "@/constants/SMT";
import { SMTTestID } from "@/constants/TestID";
import { useColorToken } from "@/hooks/useColorToken";
import { FAIconName, Icon, Text } from "@/ui/core";
import { Box, Circle, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { formatDate } from "@/utils/date.util";
import { joinTestIDs } from "@/utils/string.util";

const DayEntryPillBaseTestID = joinTestIDs(
  SMTTestID.Primary,
  SMTTestID.WeekLog,
  "pill",
);

interface DayEntryPillProps {
  date: Date;
  hasEntries: boolean;
  isPartOfStreak: boolean;
}

const DayEntryPill = ({
  date,
  hasEntries,
  isPartOfStreak,
}: DayEntryPillProps) => {
  const shouldShowBackground = hasEntries && !isPartOfStreak;

  const formattedDate = formatDate({ date, format: DateFormat.shortWeekday });
  const baseTestID = joinTestIDs(DayEntryPillBaseTestID, "day", formattedDate);

  return (
    <VStack
      alignItems="center"
      gap={8}
      justifyContent="center"
      testID={baseTestID}
    >
      <Circle
        aria-selected={hasEntries}
        backgroundColor={
          shouldShowBackground ? ColorToken.AccentBlue : ColorToken.Transparent
        }
        borderColor={
          hasEntries ? ColorToken.Transparent : ColorToken.NeutralLine
        }
        borderWidth={2}
        size={SMTWeekLogLayout.DayCircleSize}
        testID={joinTestIDs(baseTestID, "circle")}
      >
        <Icon
          color={
            hasEntries
              ? ColorToken.NeutralBackgroundPrimary
              : ColorToken.NeutralTertiary
          }
          name={hasEntries ? FAIconName.Check : FAIconName.Ellipsis}
          testID={joinTestIDs(baseTestID, "icon")}
        />
      </Circle>

      <Text testID={joinTestIDs(baseTestID, "label")} variant="caption">
        {formattedDate}
      </Text>
    </VStack>
  );
};

interface SMTStreakGradientProps {
  containerWidth: number;
  endIndex: number;
  numberOfDays: number;
  startIndex: number;
}

const SMTStreakGradient = ({
  containerWidth,
  endIndex,
  numberOfDays,
  startIndex,
}: SMTStreakGradientProps) => {
  const totalDayWidth = numberOfDays * SMTWeekLogLayout.DayCircleSize;
  const actualGap =
    containerWidth > 0
      ? (containerWidth - totalDayWidth) / (numberOfDays - 1)
      : 0;

  const streakLength = endIndex - startIndex + 1;

  const totalWidth =
    streakLength * SMTWeekLogLayout.DayCircleSize +
    (streakLength - 1) * actualGap;

  const leftPosition =
    startIndex === 0
      ? 0
      : startIndex * (SMTWeekLogLayout.DayCircleSize + actualGap);

  const gradientColorStart = useColorToken({
    colorKey: ColorToken.AccentPeriwinkle,
  });
  const gradientColorEnd = useColorToken({
    colorKey: ColorToken.AccentBlue,
  });

  return (
    <Box
      height={SMTWeekLogLayout.DayCircleSize}
      left={leftPosition}
      position="absolute"
      top={0}
      width={totalWidth}
      zIndex={0}
    >
      <LinearGradient
        colors={[gradientColorStart, gradientColorEnd]}
        end={{ x: 1, y: 0.5 }}
        start={{ x: 0, y: 0.5 }}
        style={{
          borderRadius: SMTWeekLogLayout.DayCircleSize / 2,
          height: "100%",
          overflow: "hidden",
          width: "100%",
        }}
      />
    </Box>
  );
};

export { DayEntryPill, DayEntryPillBaseTestID, SMTStreakGradient };
